import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WaitlistFormCore } from "@/components/landing/WaitlistFormCore";

const SURVEY_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSflKfkAfY6rAaSdLqqMC_xVzOm9qauyavaE_NOqO6q3ex3uqw/viewform?usp=header";

const PUBMED_URL = "https://pubmed.ncbi.nlm.nih.gov/30711122/";

// ─────────────────────────────────────────────────────────────
// SEO
// ─────────────────────────────────────────────────────────────
function applyWhySEO() {
    const prevTitle = document.title;
    document.title = "the making of Supple — why we're building it";

    const desc =
      "Why perimenopause stays invisible, and what we're building instead. By Shruti Gupta, founder of Supple.";
    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", desc);

    let linkCanonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = linkCanonical?.getAttribute("href");
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute("href", "https://supple.care/why");

    const setOG = (property, content) => {
      let m = document.querySelector(`meta[property="${property}"]`);
      const prev = m?.getAttribute("content");
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("property", property);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
      return prev;
    };
    const prevOG = {
      title: setOG("og:title", "the making of Supple — why we're building it"),
      desc: setOG("og:description", desc),
      url: setOG("og:url", "https://supple.care/why"),
    };

  return () => {
      document.title = prevTitle;
      if (prevDesc !== undefined && metaDesc)
        metaDesc.setAttribute("content", prevDesc);
      if (prevCanonical !== undefined && linkCanonical)
        linkCanonical.setAttribute("href", prevCanonical);
      if (prevOG.title !== undefined)
        document
          .querySelector('meta[property="og:title"]')
          ?.setAttribute("content", prevOG.title);
      if (prevOG.desc !== undefined)
        document
          .querySelector('meta[property="og:description"]')
          ?.setAttribute("content", prevOG.desc);
      if (prevOG.url !== undefined)
        document
          .querySelector('meta[property="og:url"]')
          ?.setAttribute("content", prevOG.url);
  };
}

function useWhySEO() {
  useEffect(applyWhySEO, []);
}

// ─────────────────────────────────────────────────────────────
// Section heading — lowercase, serif, with a coral 01/02/03 marker
// ─────────────────────────────────────────────────────────────
function SectionHeading({ index, children, testId }) {
  const num = String(index).padStart(2, "0");
  return (
    <div className="mt-14 sm:mt-16 mb-5 flex items-baseline gap-3">
      <span className="font-display text-2xl sm:text-3xl text-[#EC6A4A]/70 font-medium leading-none">
        {num}
      </span>
      <h2
        className="font-display font-medium text-2xl sm:text-[1.875rem] lg:text-[2.125rem] text-[#1C1917] normal-case"
        style={{ textTransform: "lowercase", lineHeight: 1.15 }}
        data-testid={testId}
      >
        {children}
      </h2>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────
export default function Why() {
  useWhySEO();

  return (
    <div className="bg-bg text-[#1C1917]" data-testid="why-page">
      <Header />

      <main className="pt-24 sm:pt-32 pb-0">
        {/* ── HEADER / INTRO ── */}
        <article className="max-w-2xl mx-auto px-5 sm:px-8">
          <header className="mb-10 sm:mb-14">
            <p className="eyebrow mb-4">The making of</p>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]"
              data-testid="why-headline"
              style={{ lineHeight: 1.05 }}
            >
              the making of <em>Supple</em>
            </h1>
            <p
              className="mt-5 text-base sm:text-lg text-[#44403C] leading-relaxed"
              data-testid="why-subhead"
            >
              what I&apos;m building, and why — by Shruti Gupta
            </p>
          </header>

          <div className="story-prose">
            {/* ── INTRO ── */}
            <p>
              My friends have asked me why I&apos;d start another health
              company after the first. The honest answer is that the deeper
              I personally go into perimenopause, the harder it becomes to
              look away.. It&apos;s the most common thing in the world;
              half of all humans will live through it, and somehow it
              remains one of the least seen phases of the female life.
            </p>

            <blockquote>
              One of my favourite authors, Gabor Maté, has a phrase for the
              kind of suffering that hides inside the word
              &ldquo;normal&rdquo;: too vast and too near to be noticed,
              the way water is invisible to a fish. That invisibility is
              the thing I want to attempt to undo.
            </blockquote>

            <p>
              But like all of my previous selves evolved through the
              decades, I will argue against myself first, and also, because
              I owe it that. Perimenopause is a phase (a decade long
              phase!), not a disease, and the last thing any woman needs
              is to be told a life stage is a &lsquo;condition&rsquo; that
              someone can sell her a cure for. I&apos;ve sat with that
              fact for a long time. But naming something is not the same
              as pathologising it, and silence has never protected anyone.
              The women in my own family were told their symptoms were
              normal, right up until one day the &ldquo;solution&rdquo;
              was a hysterectomy. Remove the organ, end the argument.
            </p>

            <p>
              I am building Supple so that dismissal stops being the
              standard of care. Here are the pillars of belief that I want
              to work around:
            </p>

            {/* ── SECTION 1 ── */}
            <SectionHeading index={1} testId="why-section-1-heading">
              Being told it&apos;s in your head
            </SectionHeading>
            <p>
              The first thing I kept running into, when I spoke with my
              doctors, was my own self-judgement and the certainty that
              all of it was in my own head. Maybe it was the midlife itch,
              maybe it was depression, maybe something was wrong with my
              way of being. What else can you imagine? A seemingly healthy
              woman, who looks nice but feels tired, foggy, aching, fully
              not like herself&hellip; what could be wrong with this
              woman, other than her being a moody woman? This has felt
              and will continue to feel regressive and utterly ridiculous.
            </p>
            <p>
              So, naturally, I want to change the direction of that. I
              want to turn that scattered thought of symptoms into data
              that women can hold in their hands, something with a shape
              and a slope, so that they never again have to argue her own
              body into being believed.
            </p>
            <p>
              And when it comes to relief I want every option on the
              table, with nothing withheld because &ldquo;someone&rdquo;
              decided it was too much for women, and not oversold either,
              because the honest truth is it can feel worse for a couple
              of weeks before it feels better, and women deserve to hear
              that up front. Comfort has its place&hellip; but comfort
              offered instead of the truth is only another kind of
              dismissal, and I would rather educate women first and
              soothe them second.
            </p>

            {/* ── SECTION 2 ── */}
            <SectionHeading index={2} testId="why-section-2-heading">
              Less than 7% of doctors ever felt ready for this!
            </SectionHeading>
            <p>
              I was flabbergasted when I first learned that Doctors
              actually weren&apos;t well-informed on the subject, because
              for a long while I blamed them for the wrong reason.
            </p>
            <p>
              I assumed doctors simply weren&apos;t listening, too
              overwhelmed by the long list of whines, couldn&apos;t
              confirm the actual discomfort in a 3-page essay of the
              symptoms some ladies brought them, until I looked at what
              they&apos;re actually taught, and the whole picture turned
              over.
            </p>
            <p>
              Only about one in five OB/GYN training programmes offers any
              real menopause education, and most of that is an elective
              that a student can quietly skip! (same Mayo study). When
              Mayo Clinic surveyed residents, only 6.8% reported feeling
              adequately prepared to manage women experiencing menopause.
            </p>
            <p>
              So women patients weren&apos;t always being dismissed by a
              careless doctor, but more often they were being met by a
              well-meaning one who was simply never taught the best.
            </p>
            <p>
              And it begins further back still, in 2002, when a{" "}
              <a
                href={PUBMED_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9A3F25] border-b border-[#9A3F25]/40 hover:border-[#9A3F25] transition-colors"
                data-testid="pubmed-link"
              >
                single large study
              </a>{" "}
              frightened the world off hormone therapy almost overnight;
              the one that we&apos;ve since learned was badly misread,
              drawn mostly from women already a decade past menopause, and
              a whole generation of clinicians was trained inside that
              long shadow, with the guidance only now being improved.
            </p>
            <p>So here is what I want to do about it:</p>
            <ul>
              <li>
                I want to place a valid account of data into the doctors&apos;
                hands and not only the patients&apos;, so the few who ever get
                to see one are no longer working half-blind&hellip;
              </li>
              <li>
                I want to build Supple alongside clinicians rather than
                around them, with co-developed protocols, a doctor kept in
                the loop, honest briefings.
              </li>
            </ul>

            {/* ── SECTION 3 ── */}
            <SectionHeading index={3} testId="why-section-3-heading">
              Care built for someone else&apos;s body
            </SectionHeading>
            <p>
              The thing is that most of what we call knowledge actually
              comes from Western women and Western studies, and Asian
              bodies, Asian diets, our budgets and our families were never
              really part of that sample.
            </p>
            <p>
              So what I want to build are recommendations genuinely tuned
              to how Asian women metabolise, eat and live, with all the
              household constraints that come with that, vs pasting advice
              designed for a different body!
            </p>
            <p>
              And underneath it I want to build the definitive Asian
              ecosystem for personalized health, for all of us, and that
              begins with getting to know each woman deeply: learning her
              body in her own rhythm, following her own story rather than
              the average of a thousand strangers.
            </p>

            <p>That&apos;s where our name comes from.</p>

            {/* ── SUPPLE PULL-QUOTE ── */}
            <blockquote data-testid="supple-pull-quote">
              To be &lsquo;supple&rsquo; is to bend without breaking, to
              stay soft and adaptable to change instead of braced against
              it&hellip; which is exactly what a body is trying to do
              through this transition if only we would let it.
            </blockquote>

            {/* ── CLOSING PARAS ── */}
            <p>
              And, there is a great deal here that can&apos;t be predicted
              or controlled: how a body will shift from one month to the
              next, which remedy will land and which won&apos;t, how long
              the whole passage will take. But there is so much that we
              can solve for, making the invisible visible, clearing away
              the noise and the guesswork and the endless conflicting
              advice, and letting a woman spend her attention on living
              well rather than on quietly decoding herself, alone.
            </p>
            <p>
              Supple will open to its first women later this year, in a
              few select places to begin with. In the meantime, I&apos;ll
              leave you with this: how we move through this one decade
              shapes the decades that follow more than almost anything
              else — and that is far too important to be left to chance,
              and far too personal to be handed a one-size-fits-all
              script.{" "}
              <strong className="font-semibold text-[#1C1917]">
                We&apos;re building Supple to give women the care they
                have always deserved.
              </strong>
            </p>

            {/* ── SIGN-OFF ── */}
            <p className="!mt-10">More soon.</p>
            <p>
              If you want to know more, write to me at{" "}
              <a
                href="mailto:shruti@supple.care"
                className="text-[#9A3F25] border-b border-[#9A3F25]/40 hover:border-[#9A3F25] transition-colors"
                data-testid="shruti-email-link"
              >
                shruti@supple.care
              </a>
              .
            </p>
            <p className="!mt-8 text-sm font-semibold tracking-wide text-[#9A3F25]">
              — Shruti, founder
            </p>
          </div>
        </article>

        {/* ── END-OF-PAGE CTA BLOCK ── */}
        <section
          className="mt-16 sm:mt-24 py-16 sm:py-20"
          style={{ backgroundColor: "var(--surface-alt)" }}
          data-testid="why-cta-section"
        >
          <div className="max-w-2xl mx-auto px-5 sm:px-8">
            <p className="eyebrow mb-4">Join us</p>
            <h2
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1C1917]"
              style={{ lineHeight: 1.1 }}
            >
              If you&apos;d like to be there{" "}
              <em>from the very beginning</em>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#44403C] leading-relaxed">
              Come and build it with us, and help share your struggles of
              perimenopause to menopause and beyond.
            </p>

            <div className="mt-8 supple-card" id="waitlist">
              <WaitlistFormCore testIdPrefix="why-waitlist" />
            </div>

            <div className="mt-8 text-center sm:text-left">
              <a
                href={SURVEY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#9A3F25] font-semibold text-sm sm:text-base border-b border-[#9A3F25]/30 hover:border-[#9A3F25] transition-colors pb-0.5"
                data-testid="why-share-story-link"
              >
                Share your perimenopause story{" "}
                <ArrowUpRight size={16} />
              </a>
            </div>

            <p className="mt-10 text-center">
              <Link
                to="/"
                className="text-sm text-[#78716C] hover:text-[#9A3F25] transition-colors"
                data-testid="why-back-home"
              >
                ← Back to home
              </Link>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
